import {useState} from 'react';
import {FileText, Upload} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@common-web/components/ui/dialog';
import {Button} from '@common-web/components/ui/button';
import {Label} from '@common-web/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {cn} from '@common-web/lib/utils';
import {NOT_BUILT, PreviewDot} from '@internal/pages/group-pipeline/components/PreviewDot';
import {FolderChip} from '@internal/pages/group-pipeline/detail/ftp/FolderChip';

const KINDS = [
  {prefix: 'P', label: 'Payroll'},
  {prefix: 'C', label: 'Census'},
] as const;

// Not built yet: shows how a payroll or census drop would work. The file gets the prefix CBS's
// automation looks for, so it's always picked up. Nothing is uploaded.
export function PayrollUploadDialog({
  folders,
  onOpenChange,
}: {
  folders: string[];
  onOpenChange: (open: boolean) => void;
}) {
  const [prefix, setPrefix] = useState<'P' | 'C'>('P');
  const [folder, setFolder] = useState(folders[0]);
  const [file, setFile] = useState<File | null>(null);
  const savedAs = file && `${prefix}_${file.name.replace(/^[pc]_/i, '')}`;

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>Upload payroll or census</DialogTitle>
          <DialogDescription>
            Saves to {folder && <FolderChip name={folder} />} for CBS's automation to pick up.
          </DialogDescription>
        </DialogHeader>

        <div className='flex flex-col gap-1.5'>
          <Label>Type</Label>
          <div className='inline-flex w-fit rounded-md bg-gray-100 p-1'>
            {KINDS.map((kind) => (
              <button
                key={kind.prefix}
                type='button'
                aria-pressed={prefix === kind.prefix}
                onClick={() => setPrefix(kind.prefix)}
                className={cn(
                  'rounded px-3 py-1 text-sm',
                  prefix === kind.prefix ? 'bg-white font-medium shadow-sm' : 'text-gray-600',
                )}
              >
                {kind.label}
              </button>
            ))}
          </div>
        </div>

        {folders.length > 1 && (
          <div className='flex flex-col gap-1.5'>
            <Label htmlFor='payroll-folder'>Folder</Label>
            <Select value={folder} onValueChange={setFolder}>
              <SelectTrigger id='payroll-folder'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {folders.map((name) => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <label className='flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border border-dashed border-gray-300 px-4 py-8 text-center text-sm hover:bg-gray-50'>
          <input
            type='file'
            className='sr-only'
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          {file ? (
            <>
              <FileText className='h-6 w-6 text-gray-400' />
              <span className='break-all font-medium'>{file.name}</span>
              <span className='text-xs text-gray-500'>Choose a different file</span>
            </>
          ) : (
            <>
              <Upload className='h-6 w-6 text-gray-400' />
              <span className='font-medium'>Choose a file</span>
            </>
          )}
        </label>

        {savedAs && (
          <p className='text-sm text-gray-600'>
            Saved as <span className='break-all font-mono text-xs'>{savedAs}</span>
          </p>
        )}
        <p className='text-xs text-amber-800'>
          Uploading payroll and census files isn't available yet.
        </p>

        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled className='relative' title={NOT_BUILT}>
            Upload
            <PreviewDot />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
